import React, { FC, useMemo, useState } from "react";
import styled from "@emotion/styled";
import { useQuery } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import { Button, Dialog, Flex, Tabs, Text } from "@radix-ui/themes";
import { DateTime } from "luxon";

import { atlasRoutes } from "@/app/router/routes";
import { api } from "@/modules/api";
import { DateTimeFieldRow } from "@/components/table/datetime-field-row";
import { UuidFieldRow } from "@/components/table/uuid-field-row";
import { FlipDotLoader, FlipDotLoaderKind } from "@/components/flip-dot-loader/flip-dot-loader";

type TabKey = "connections" | "calendars" | "events";

type CalendarConnectionRow = {
  id: string;
  associated_space_account_id: string;
  associated_email_address?: string | null;
  provider_subject_id?: string | null;
  provider_kind: string;
  is_connected?: boolean | null;
  value_json: Record<string, unknown>;
  created_at: string;
  updated_at: string;
};

type ConnectedCalendarRow = {
  id: string;
  associated_calendar_connection_id: string;
  associated_space_account_id?: string | null;
  provider_kind?: string | null;
  external_calendar_id: string;
  primary_label?: string | null;
  webhook_status?: string | null;
  sync_started_at?: string | null;
  sync_ended_at?: string | null;
  last_sync_ended_with_error?: boolean | null;
  reconciled_at?: string | null;
  value_json: Record<string, unknown>;
  created_at: string;
  updated_at: string;
};

type ConnectedCalendarEventRow = {
  id: string;
  associated_connected_calendar_id: string;
  associated_calendar_connection_id?: string | null;
  associated_space_account_id?: string | null;
  provider_kind?: string | null;
  linked_note_id?: string | null;
  external_event_id: string;
  start_time?: string | null;
  end_time?: string | null;
  is_all_day: boolean;
  primary_label?: string | null;
  self_response_status?: string | null;
  calendar_name?: string | null;
  calendar_background_color?: string | null;
  reconciled_at?: string | null;
  soft_deleted_at?: string | null;
  value_json: Record<string, unknown>;
  created_at: string;
  updated_at: string;
};

type MeetingBriefingAttendee = {
  email: string;
  display_name?: string | null;
};

type MeetingBriefingMeetingDetails = {
  title?: string | null;
  start_time?: string | null;
  end_time?: string | null;
  is_all_day: boolean;
  attendees: MeetingBriefingAttendee[];
  description?: string | null;
};

type MeetingBriefingOutput = {
  markdown: string;
  note_ids: string[];
  reasoning: string;
};

type MeetingBriefingReferencedNote = {
  id: string;
  title?: string | null;
};

type MeetingBriefingToolCall = {
  tool: string;
  args?: Record<string, unknown> | null;
  timestamp?: string | null;
  result_count?: number | null;
  content_length?: number | null;
  error?: string | null;
};

type MeetingBriefingTiming = {
  tool_calls: number;
  total_time_seconds: number;
};

type MeetingBriefingPreviewResponse = {
  event_id: string;
  space_account_id: string;
  model: string;
  meeting_details: MeetingBriefingMeetingDetails;
  output: MeetingBriefingOutput;
  referenced_notes: MeetingBriefingReferencedNote[];
  tool_calls: MeetingBriefingToolCall[];
  timing: MeetingBriefingTiming;
  json_payload: Record<string, unknown>;
};

type ConnectionFilters = {
  query: string;
  spaceAccountId: string;
};

type CalendarFilters = {
  query: string;
  spaceAccountId: string;
  calendarConnectionId: string;
};

type EventFilters = {
  query: string;
  spaceAccountId: string;
  calendarId: string;
  calendarConnectionId: string;
  dateFrom: string;
  dateTo: string;
  includeSoftDeleted: boolean;
};

const DEFAULT_PAGE_SIZE = 50;

const formatBoolean = (value?: boolean | null) => {
  if (value === null || value === undefined) return "-";
  return value ? "Yes" : "No";
};

const postInternal = api.rawPost as unknown as <T>(
  path: string,
  options: { body: Record<string, unknown> }
) => Promise<{ data: T }>;

const toIso = (value: string) => {
  if (!value) return null;
  const parsed = DateTime.fromISO(value);
  return parsed.isValid ? parsed.toUTC().toISO() : null;
};

const formatMeetingTimeRange = (start?: string | null, end?: string | null) => {
  if (!start && !end) return null;
  const startDt = start ? DateTime.fromISO(start) : null;
  const endDt = end ? DateTime.fromISO(end) : null;

  if (startDt?.isValid && endDt?.isValid) {
    return `${startDt.toFormat("yyyy-LL-dd HH:mm")} - ${endDt.toFormat("HH:mm")}`;
  }
  if (startDt?.isValid) {
    return startDt.toFormat("yyyy-LL-dd HH:mm");
  }
  return null;
};

const formatAttendees = (attendees: MeetingBriefingAttendee[]) => {
  if (!attendees.length) return "None";
  return attendees
    .map(attendee => attendee.display_name ?? attendee.email)
    .filter(Boolean)
    .join(", ");
};

const truncateDescription = (description: string, limit = 200) => {
  if (description.length <= limit) return description;
  return `${description.slice(0, limit)}...`;
};

const buildMeetingBriefingOutputText = (
  data: MeetingBriefingPreviewResponse,
  includeAgentTrace: boolean
) => {
  const lines: string[] = [];
  const meeting = data.meeting_details;
  const title = meeting.title ?? "Untitled";
  const timeRange = formatMeetingTimeRange(meeting.start_time, meeting.end_time);
  const attendees = formatAttendees(meeting.attendees);
  const description = meeting.description ? truncateDescription(meeting.description) : null;

  lines.push("=".repeat(60));
  lines.push("MEETING DETAILS");
  lines.push("=".repeat(60));
  lines.push(`Title: ${title}`);
  if (timeRange) {
    lines.push(`Time: ${timeRange}`);
  }
  lines.push(`Attendees: ${attendees}`);
  if (description) {
    lines.push(`Description: ${description}`);
  }

  lines.push("");
  lines.push("=".repeat(60));
  lines.push(`Generating briefing with model: ${data.model}`);
  lines.push("=".repeat(60));
  lines.push("");

  if (includeAgentTrace) {
    lines.push("=".repeat(60));
    lines.push("AGENT TRACE");
    lines.push("=".repeat(60));
    data.tool_calls.forEach((call, index) => {
      const args = call.args ? JSON.stringify(call.args) : "{}";
      lines.push("");
      lines.push(`[${index + 1}] ${call.tool}(${args})`);
      if (call.result_count !== undefined && call.result_count !== null) {
        lines.push(`    -> Found ${call.result_count} results`);
      }
      if (call.content_length !== undefined && call.content_length !== null) {
        lines.push(`    -> Read ${call.content_length} characters`);
      }
      if (call.error) {
        lines.push(`    -> Error: ${call.error}`);
      }
    });
    lines.push("");
  }

  lines.push("=".repeat(60));
  lines.push("GENERATED BRIEFING");
  lines.push("=".repeat(60));
  lines.push("");
  lines.push(data.output.markdown);
  lines.push("");

  lines.push("-".repeat(40));
  lines.push("REFERENCED NOTES");
  lines.push("-".repeat(40));
  if (data.referenced_notes.length > 0) {
    data.referenced_notes.forEach((note, index) => {
      lines.push(`  ${index + 1}. ${note.title ?? "Unknown"} (${note.id})`);
    });
  } else {
    lines.push("  (No notes referenced)");
  }
  lines.push("");

  lines.push("-".repeat(40));
  lines.push("AGENT REASONING");
  lines.push("-".repeat(40));
  lines.push("");
  lines.push(data.output.reasoning || "");
  lines.push("");

  lines.push("-".repeat(40));
  lines.push("TIMING");
  lines.push("-".repeat(40));
  lines.push(`Tool calls: ${data.timing.tool_calls}`);
  lines.push(`Total time: ${data.timing.total_time_seconds.toFixed(2)}s`);
  lines.push("");

  lines.push("=".repeat(60));
  lines.push("JSONB PAYLOAD (for storage)");
  lines.push("=".repeat(60));
  lines.push(JSON.stringify(data.json_payload, null, 2));

  return lines.join("\n");
};

const renderJsonPreview = (value: Record<string, unknown> | null | undefined) => {
  if (!value || Object.keys(value).length === 0) {
    return <span>-</span>;
  }
  const json = JSON.stringify(value, null, 2);
  return (
    <details>
      <summary>View</summary>
      <JsonPre>{json}</JsonPre>
    </details>
  );
};

const DateCell: FC<{ date?: string | null }> = ({ date }) => {
  if (!date) return <span>-</span>;
  return <DateTimeFieldRow dateStr={date} />;
};

const PaginationControls: FC<{
  total: number;
  offset: number;
  limit: number;
  onPrev: () => void;
  onNext: () => void;
}> = ({ total, offset, limit, onPrev, onNext }) => {
  if (total <= limit) return null;
  return (
    <PaginationRow>
      <Text size="2">
        Showing {offset + 1}-{Math.min(offset + limit, total)} of {total}
      </Text>
      <Flex gap="2">
        <Button variant="soft" onClick={onPrev} disabled={offset === 0}>
          Previous
        </Button>
        <Button variant="soft" onClick={onNext} disabled={offset + limit >= total}>
          Next
        </Button>
      </Flex>
    </PaginationRow>
  );
};

export const CalendarsRootPage: FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTabParam = searchParams.get("tab");
  const initialTab = ["connections", "calendars", "events"].includes(initialTabParam as TabKey)
    ? (initialTabParam as TabKey)
    : "connections";
  const [activeTab, setActiveTab] = useState<TabKey>(initialTab);

  const [connectionInput, setConnectionInput] = useState<ConnectionFilters>({
    query: "",
    spaceAccountId: "",
  });
  const [connectionSearch, setConnectionSearch] = useState<ConnectionFilters>({
    query: "",
    spaceAccountId: "",
  });
  const [connectionOffset, setConnectionOffset] = useState(0);

  const [calendarInput, setCalendarInput] = useState<CalendarFilters>({
    query: "",
    spaceAccountId: "",
    calendarConnectionId: "",
  });
  const [calendarSearch, setCalendarSearch] = useState<CalendarFilters>({
    query: "",
    spaceAccountId: "",
    calendarConnectionId: "",
  });
  const [calendarOffset, setCalendarOffset] = useState(0);

  const [eventInput, setEventInput] = useState<EventFilters>({
    query: "",
    spaceAccountId: "",
    calendarId: "",
    calendarConnectionId: "",
    dateFrom: "",
    dateTo: "",
    includeSoftDeleted: false,
  });
  const [eventSearch, setEventSearch] = useState<EventFilters>({
    query: "",
    spaceAccountId: "",
    calendarId: "",
    calendarConnectionId: "",
    dateFrom: "",
    dateTo: "",
    includeSoftDeleted: false,
  });
  const [eventOffset, setEventOffset] = useState(0);
  const [briefingModalOpen, setBriefingModalOpen] = useState(false);
  const [briefingEventId, setBriefingEventId] = useState<string | null>(null);
  const [briefingData, setBriefingData] = useState<MeetingBriefingPreviewResponse | null>(null);
  const [briefingLoading, setBriefingLoading] = useState(false);
  const [briefingError, setBriefingError] = useState<string | null>(null);
  const [briefingShowTrace, setBriefingShowTrace] = useState(false);

  const handleTabChange = (value: string) => {
    const nextTab = value as TabKey;
    setActiveTab(nextTab);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("tab", nextTab);
    setSearchParams(nextParams);
  };

  const hasConnectionCriteria =
    Boolean(connectionSearch.query.trim()) || Boolean(connectionSearch.spaceAccountId.trim());

  const connectionQuery = useQuery({
    queryKey: ["calendar-connections-search", connectionSearch, connectionOffset],
    queryFn: async () => {
      const body: Record<string, unknown> = {
        limit: DEFAULT_PAGE_SIZE,
        offset: connectionOffset,
      };
      if (connectionSearch.query.trim()) body.query = connectionSearch.query.trim();
      if (connectionSearch.spaceAccountId.trim()) {
        body.filter_by_space_account_id = connectionSearch.spaceAccountId.trim();
      }
      return postInternal<{ results: CalendarConnectionRow[]; total: number }>(
        "/internal/v1/calendar/calendar_connections/search",
        { body }
      );
    },
    enabled: hasConnectionCriteria,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  const connectionRows = useMemo<CalendarConnectionRow[]>(
    () => connectionQuery.data?.data?.results ?? [],
    [connectionQuery.data]
  );
  const connectionTotal = connectionQuery.data?.data?.total ?? 0;

  const hasCalendarCriteria =
    Boolean(calendarSearch.query.trim()) ||
    Boolean(calendarSearch.spaceAccountId.trim()) ||
    Boolean(calendarSearch.calendarConnectionId.trim());

  const calendarQuery = useQuery({
    queryKey: ["connected-calendars-search", calendarSearch, calendarOffset],
    queryFn: async () => {
      const body: Record<string, unknown> = {
        limit: DEFAULT_PAGE_SIZE,
        offset: calendarOffset,
      };
      if (calendarSearch.query.trim()) body.query = calendarSearch.query.trim();
      if (calendarSearch.spaceAccountId.trim()) {
        body.filter_by_space_account_id = calendarSearch.spaceAccountId.trim();
      }
      if (calendarSearch.calendarConnectionId.trim()) {
        body.filter_by_calendar_connection_id = calendarSearch.calendarConnectionId.trim();
      }
      return postInternal<{ results: ConnectedCalendarRow[]; total: number }>(
        "/internal/v1/calendar/calendars/search",
        { body }
      );
    },
    enabled: hasCalendarCriteria,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  const calendarRows = useMemo<ConnectedCalendarRow[]>(
    () => calendarQuery.data?.data?.results ?? [],
    [calendarQuery.data]
  );
  const calendarTotal = calendarQuery.data?.data?.total ?? 0;

  const hasEventCriteria =
    Boolean(eventSearch.query.trim()) ||
    Boolean(eventSearch.spaceAccountId.trim()) ||
    Boolean(eventSearch.calendarId.trim()) ||
    Boolean(eventSearch.calendarConnectionId.trim()) ||
    Boolean(eventSearch.dateFrom.trim()) ||
    Boolean(eventSearch.dateTo.trim());

  const eventQuery = useQuery({
    queryKey: ["connected-calendar-events-search", eventSearch, eventOffset],
    queryFn: async () => {
      const body: Record<string, unknown> = {
        limit: DEFAULT_PAGE_SIZE,
        offset: eventOffset,
        include_soft_deleted: eventSearch.includeSoftDeleted,
      };
      if (eventSearch.query.trim()) body.query = eventSearch.query.trim();
      if (eventSearch.spaceAccountId.trim()) {
        body.filter_by_space_account_id = eventSearch.spaceAccountId.trim();
      }
      if (eventSearch.calendarId.trim()) {
        body.filter_by_calendar_id = eventSearch.calendarId.trim();
      }
      if (eventSearch.calendarConnectionId.trim()) {
        body.filter_by_calendar_connection_id = eventSearch.calendarConnectionId.trim();
      }
      const dateFrom = toIso(eventSearch.dateFrom.trim());
      const dateTo = toIso(eventSearch.dateTo.trim());
      if (dateFrom) body.date_from = dateFrom;
      if (dateTo) body.date_to = dateTo;
      return postInternal<{ results: ConnectedCalendarEventRow[]; total: number }>(
        "/internal/v1/calendar/calendar_events/search",
        { body }
      );
    },
    enabled: hasEventCriteria,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  const eventRows = useMemo<ConnectedCalendarEventRow[]>(
    () => eventQuery.data?.data?.results ?? [],
    [eventQuery.data]
  );
  const eventTotal = eventQuery.data?.data?.total ?? 0;

  const applyConnectionSearch = () => {
    setConnectionSearch({ ...connectionInput });
    setConnectionOffset(0);
  };

  const applyCalendarSearch = () => {
    setCalendarSearch({ ...calendarInput });
    setCalendarOffset(0);
  };

  const applyEventSearch = () => {
    setEventSearch({ ...eventInput });
    setEventOffset(0);
  };

  const focusCalendarsForConnection = (connectionId: string) => {
    setActiveTab("calendars");
    const nextFilters = {
      query: "",
      spaceAccountId: "",
      calendarConnectionId: connectionId,
    };
    setCalendarInput(nextFilters);
    setCalendarSearch(nextFilters);
    setCalendarOffset(0);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("tab", "calendars");
    setSearchParams(nextParams);
  };

  const focusEventsForCalendar = (calendarId: string) => {
    setActiveTab("events");
    setEventInput(prev => ({ ...prev, calendarId, query: "" }));
    setEventSearch(prev => ({ ...prev, calendarId, query: "" }));
    setEventOffset(0);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("tab", "events");
    setSearchParams(nextParams);
  };

  const focusCalendarsForEvent = (calendarId: string) => {
    setActiveTab("calendars");
    setCalendarInput({
      query: calendarId,
      spaceAccountId: "",
      calendarConnectionId: "",
    });
    setCalendarSearch({
      query: calendarId,
      spaceAccountId: "",
      calendarConnectionId: "",
    });
    setCalendarOffset(0);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("tab", "calendars");
    setSearchParams(nextParams);
  };

  const runMeetingBriefingPreview = async (eventId: string) => {
    setBriefingLoading(true);
    setBriefingError(null);
    try {
      const response = await postInternal<MeetingBriefingPreviewResponse>(
        `/internal/v1/calendar/calendar_events/${eventId}/meeting_briefing/test`,
        { body: { model: "sonnet" } }
      );
      setBriefingData(response.data);
    } catch (error) {
      setBriefingError(error instanceof Error ? error.message : String(error));
    } finally {
      setBriefingLoading(false);
    }
  };

  const openMeetingBriefingModal = (row: ConnectedCalendarEventRow) => {
    setBriefingEventId(row.id);
    setBriefingData(null);
    setBriefingShowTrace(false);
    setBriefingModalOpen(true);
    runMeetingBriefingPreview(row.id);
  };

  const closeMeetingBriefingModal = () => {
    setBriefingModalOpen(false);
  };

  const handleRegenerateBriefing = () => {
    if (!briefingEventId) return;
    setBriefingData(null);
    setBriefingError(null);
    runMeetingBriefingPreview(briefingEventId);
  };

  return (
    <Container>
      <Flex justify="between" align="center" mb="4">
        <div>
          <h1>Calendars</h1>
          <DescriptionText>
            Search calendar connections, calendars, and events by IDs or Space Account.
          </DescriptionText>
        </div>
      </Flex>

      <Tabs.Root value={activeTab} onValueChange={handleTabChange}>
        <Tabs.List>
          <Tabs.Trigger value="connections">Connections</Tabs.Trigger>
          <Tabs.Trigger value="calendars">Calendars</Tabs.Trigger>
          <Tabs.Trigger value="events">Events</Tabs.Trigger>
        </Tabs.List>

        <Tabs.Content value="connections">
          <SectionHeader>
            <h2>Calendar Connections</h2>
          </SectionHeader>
          <SearchRow>
            <Input
              value={connectionInput.query}
              onChange={e => setConnectionInput(prev => ({ ...prev, query: e.target.value }))}
              onKeyDown={e => {
                if (e.key === "Enter") applyConnectionSearch();
              }}
              placeholder="Search by Connection ID or Space Account ID"
            />
            <Input
              value={connectionInput.spaceAccountId}
              onChange={e =>
                setConnectionInput(prev => ({ ...prev, spaceAccountId: e.target.value }))
              }
              onKeyDown={e => {
                if (e.key === "Enter") applyConnectionSearch();
              }}
              placeholder="Space Account ID (optional)"
            />
            <Button onClick={applyConnectionSearch}>Search</Button>
          </SearchRow>

          {connectionQuery.isLoading && <div>Loading...</div>}
          {connectionQuery.isError && (
            <div>Error fetching connections: {String(connectionQuery.error)}</div>
          )}

          {connectionRows.length > 0 ? (
            <>
              <TableContainer>
                <Table>
                  <thead>
                    <tr>
                      <th>Created</th>
                      <th>ID</th>
                      <th>Space Account</th>
                      <th>Provider</th>
                      <th>Provider Subject</th>
                      <th>Email</th>
                      <th>Connected?</th>
                      <th>Value</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {connectionRows.map(row => (
                      <tr key={row.id}>
                        <td>
                          <DateCell date={row.created_at} />
                        </td>
                        <td>
                          <UuidFieldRow id={row.id} />
                        </td>
                        <td>
                          <UuidFieldRow id={row.associated_space_account_id} />
                        </td>
                        <td>{row.provider_kind}</td>
                        <td>{row.provider_subject_id ?? "-"}</td>
                        <td>{row.associated_email_address ?? "-"}</td>
                        <td>{formatBoolean(row.is_connected)}</td>
                        <td>{renderJsonPreview(row.value_json)}</td>
                        <td>
                          <Button
                            variant="soft"
                            onClick={() => focusCalendarsForConnection(row.id)}
                          >
                            View Calendars
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </TableContainer>
              <PaginationControls
                total={connectionTotal}
                offset={connectionOffset}
                limit={DEFAULT_PAGE_SIZE}
                onPrev={() =>
                  setConnectionOffset(Math.max(0, connectionOffset - DEFAULT_PAGE_SIZE))
                }
                onNext={() =>
                  connectionOffset + DEFAULT_PAGE_SIZE < connectionTotal &&
                  setConnectionOffset(connectionOffset + DEFAULT_PAGE_SIZE)
                }
              />
            </>
          ) : (
            !connectionQuery.isLoading && hasConnectionCriteria && <div>No connections found.</div>
          )}
        </Tabs.Content>

        <Tabs.Content value="calendars">
          <SectionHeader>
            <h2>Connected Calendars</h2>
          </SectionHeader>
          <SearchRow>
            <Input
              value={calendarInput.query}
              onChange={e => setCalendarInput(prev => ({ ...prev, query: e.target.value }))}
              onKeyDown={e => {
                if (e.key === "Enter") applyCalendarSearch();
              }}
              placeholder="Search by Calendar ID, Connection ID, or Space Account ID"
            />
            <Input
              value={calendarInput.spaceAccountId}
              onChange={e =>
                setCalendarInput(prev => ({ ...prev, spaceAccountId: e.target.value }))
              }
              onKeyDown={e => {
                if (e.key === "Enter") applyCalendarSearch();
              }}
              placeholder="Space Account ID (optional)"
            />
            <Input
              value={calendarInput.calendarConnectionId}
              onChange={e =>
                setCalendarInput(prev => ({ ...prev, calendarConnectionId: e.target.value }))
              }
              onKeyDown={e => {
                if (e.key === "Enter") applyCalendarSearch();
              }}
              placeholder="Calendar Connection ID (optional)"
            />
            <Button onClick={applyCalendarSearch}>Search</Button>
          </SearchRow>

          {calendarQuery.isLoading && <div>Loading...</div>}
          {calendarQuery.isError && (
            <div>Error fetching calendars: {String(calendarQuery.error)}</div>
          )}

          {calendarRows.length > 0 ? (
            <>
              <TableContainer>
                <Table>
                  <thead>
                    <tr>
                      <th>Created</th>
                      <th>ID</th>
                      <th>Connection</th>
                      <th>Space Account</th>
                      <th>Provider</th>
                      <th>External ID</th>
                      <th>Primary Label</th>
                      <th>Webhook Status</th>
                      <th>Sync Started</th>
                      <th>Sync Ended</th>
                      <th>Sync Error?</th>
                      <th>Value</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {calendarRows.map(row => (
                      <tr key={row.id}>
                        <td>
                          <DateCell date={row.created_at} />
                        </td>
                        <td>
                          <UuidFieldRow id={row.id} />
                        </td>
                        <td>
                          <UuidFieldRow id={row.associated_calendar_connection_id} />
                        </td>
                        <td>
                          <UuidFieldRow id={row.associated_space_account_id ?? null} />
                        </td>
                        <td>{row.provider_kind ?? "-"}</td>
                        <td>{row.external_calendar_id}</td>
                        <td>{row.primary_label ?? "-"}</td>
                        <td>{row.webhook_status ?? "-"}</td>
                        <td>
                          <DateCell date={row.sync_started_at ?? null} />
                        </td>
                        <td>
                          <DateCell date={row.sync_ended_at ?? null} />
                        </td>
                        <td>{formatBoolean(row.last_sync_ended_with_error)}</td>
                        <td>{renderJsonPreview(row.value_json)}</td>
                        <td>
                          <Button variant="soft" onClick={() => focusEventsForCalendar(row.id)}>
                            View Events
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </TableContainer>
              <PaginationControls
                total={calendarTotal}
                offset={calendarOffset}
                limit={DEFAULT_PAGE_SIZE}
                onPrev={() => setCalendarOffset(Math.max(0, calendarOffset - DEFAULT_PAGE_SIZE))}
                onNext={() =>
                  calendarOffset + DEFAULT_PAGE_SIZE < calendarTotal &&
                  setCalendarOffset(calendarOffset + DEFAULT_PAGE_SIZE)
                }
              />
            </>
          ) : (
            !calendarQuery.isLoading && hasCalendarCriteria && <div>No calendars found.</div>
          )}
        </Tabs.Content>

        <Tabs.Content value="events">
          <SectionHeader>
            <h2>Connected Calendar Events</h2>
          </SectionHeader>
          <SearchRow>
            <Input
              value={eventInput.query}
              onChange={e => setEventInput(prev => ({ ...prev, query: e.target.value }))}
              onKeyDown={e => {
                if (e.key === "Enter") applyEventSearch();
              }}
              placeholder="Search by Event ID, Calendar ID, Connection ID, or Space Account ID"
            />
            <Input
              value={eventInput.spaceAccountId}
              onChange={e => setEventInput(prev => ({ ...prev, spaceAccountId: e.target.value }))}
              onKeyDown={e => {
                if (e.key === "Enter") applyEventSearch();
              }}
              placeholder="Space Account ID (optional)"
            />
            <Input
              value={eventInput.calendarId}
              onChange={e => setEventInput(prev => ({ ...prev, calendarId: e.target.value }))}
              onKeyDown={e => {
                if (e.key === "Enter") applyEventSearch();
              }}
              placeholder="Calendar ID (optional)"
            />
            <Input
              value={eventInput.calendarConnectionId}
              onChange={e =>
                setEventInput(prev => ({ ...prev, calendarConnectionId: e.target.value }))
              }
              onKeyDown={e => {
                if (e.key === "Enter") applyEventSearch();
              }}
              placeholder="Connection ID (optional)"
            />
            <Input
              type="datetime-local"
              value={eventInput.dateFrom}
              onChange={e => setEventInput(prev => ({ ...prev, dateFrom: e.target.value }))}
            />
            <Input
              type="datetime-local"
              value={eventInput.dateTo}
              onChange={e => setEventInput(prev => ({ ...prev, dateTo: e.target.value }))}
            />
            <CheckboxRow>
              <input
                type="checkbox"
                checked={eventInput.includeSoftDeleted}
                onChange={e =>
                  setEventInput(prev => ({ ...prev, includeSoftDeleted: e.target.checked }))
                }
              />
              <span>Include deleted</span>
            </CheckboxRow>
            <Button onClick={applyEventSearch}>Search</Button>
          </SearchRow>

          {eventQuery.isLoading && <div>Loading...</div>}
          {eventQuery.isError && <div>Error fetching events: {String(eventQuery.error)}</div>}

          {eventRows.length > 0 ? (
            <>
              <TableContainer>
                <Table>
                  <thead>
                    <tr>
                      <th>Start</th>
                      <th>End</th>
                      <th>ID</th>
                      <th>Calendar</th>
                      <th>Connection</th>
                      <th>Space Account</th>
                      <th>Provider</th>
                      <th>External ID</th>
                      <th>Primary Label</th>
                      <th>All Day?</th>
                      <th>Self Status</th>
                      <th>Soft Deleted</th>
                      <th>Value</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {eventRows.map(row => (
                      <tr key={row.id}>
                        <td>
                          <DateCell date={row.start_time ?? null} />
                        </td>
                        <td>
                          <DateCell date={row.end_time ?? null} />
                        </td>
                        <td>
                          <UuidFieldRow id={row.id} />
                        </td>
                        <td>
                          <UuidFieldRow id={row.associated_connected_calendar_id} />
                        </td>
                        <td>
                          <UuidFieldRow id={row.associated_calendar_connection_id ?? null} />
                        </td>
                        <td>
                          <UuidFieldRow id={row.associated_space_account_id ?? null} />
                        </td>
                        <td>{row.provider_kind ?? "-"}</td>
                        <td>{row.external_event_id}</td>
                        <td>{row.primary_label ?? "-"}</td>
                        <td>{formatBoolean(row.is_all_day)}</td>
                        <td>{row.self_response_status ?? "-"}</td>
                        <td>
                          <DateCell date={row.soft_deleted_at ?? null} />
                        </td>
                        <td>{renderJsonPreview(row.value_json)}</td>
                        <td>
                          <Flex gap="2" wrap="wrap">
                            <Button
                              variant="soft"
                              onClick={() =>
                                focusCalendarsForEvent(row.associated_connected_calendar_id)
                              }
                            >
                              View Calendar
                            </Button>
                            {row.linked_note_id && (
                              <Button variant="soft" asChild>
                                <Link
                                  to={
                                    atlasRoutes.notesView({
                                      params: { noteId: row.linked_note_id },
                                    }).path
                                  }
                                >
                                  View Note
                                </Link>
                              </Button>
                            )}
                            <Button variant="soft" onClick={() => openMeetingBriefingModal(row)}>
                              Meeting Briefing
                            </Button>
                          </Flex>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </TableContainer>
              <PaginationControls
                total={eventTotal}
                offset={eventOffset}
                limit={DEFAULT_PAGE_SIZE}
                onPrev={() => setEventOffset(Math.max(0, eventOffset - DEFAULT_PAGE_SIZE))}
                onNext={() =>
                  eventOffset + DEFAULT_PAGE_SIZE < eventTotal &&
                  setEventOffset(eventOffset + DEFAULT_PAGE_SIZE)
                }
              />
            </>
          ) : (
            !eventQuery.isLoading && hasEventCriteria && <div>No events found.</div>
          )}
        </Tabs.Content>
      </Tabs.Root>

      <Dialog.Root open={briefingModalOpen} onOpenChange={closeMeetingBriefingModal}>
        <Dialog.Content
          style={{ maxWidth: "95vw", width: "1000px", maxHeight: "85vh", borderRadius: "12px" }}
        >
          <DialogTitle>Meeting Briefing Preview</DialogTitle>
          <ModalBody>
            {briefingLoading && (
              <LoaderWrapper>
                <FlipDotLoader kind={FlipDotLoaderKind.Loading} />
              </LoaderWrapper>
            )}
            {briefingError && <ErrorText size="2">Failed: {briefingError}</ErrorText>}
            {briefingData && (
              <>
                <Flex align="center" gap="3" mb="3" wrap="wrap">
                  <Text size="2">
                    Model: <strong>{briefingData.model}</strong>
                  </Text>
                  <CheckboxRow>
                    <input
                      type="checkbox"
                      checked={briefingShowTrace}
                      onChange={e => setBriefingShowTrace(e.target.checked)}
                    />
                    <span>Include agent trace</span>
                  </CheckboxRow>
                </Flex>
                <OutputContainer>
                  <pre>{buildMeetingBriefingOutputText(briefingData, briefingShowTrace)}</pre>
                </OutputContainer>
              </>
            )}
          </ModalBody>

          <ActionButtons>
            <Button
              variant="soft"
              onClick={handleRegenerateBriefing}
              disabled={briefingLoading || !briefingEventId}
            >
              Regenerate
            </Button>
            <Button onClick={closeMeetingBriefingModal}>Close</Button>
          </ActionButtons>
        </Dialog.Content>
      </Dialog.Root>
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
`;

const DescriptionText = styled(Text)`
  display: block;
  color: var(--gray-12);
`;

const SectionHeader = styled.div`
  margin-top: 16px;
  margin-bottom: 8px;
`;

const SearchRow = styled(Flex)`
  gap: 8px;
  margin: 12px 0 16px 0;
  flex-wrap: wrap;
`;

const Input = styled.input`
  min-width: 240px;
  padding: 8px 10px;
  border: 1px solid var(--gray-6);
  border-radius: 6px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 1100px;

  th,
  td {
    text-align: left;
    padding: 8px 6px;
    border-bottom: 1px solid var(--gray-5);
    white-space: nowrap;
    vertical-align: top;
  }

  details {
    cursor: pointer;
  }

  summary {
    list-style: none;
  }

  summary::-webkit-details-marker {
    display: none;
  }
`;

const TableContainer = styled.div`
  width: 100%;
  overflow-x: auto;
`;

const JsonPre = styled.pre`
  margin-top: 6px;
  padding: 8px;
  background: var(--gray-2);
  border: 1px solid var(--gray-4);
  border-radius: 6px;
  max-width: 420px;
  max-height: 200px;
  overflow: auto;
  font-size: 11px;
`;

const PaginationRow = styled(Flex)`
  justify-content: space-between;
  align-items: center;
  margin-top: 12px;
`;

const CheckboxRow = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
`;

const DialogTitle = styled(Dialog.Title)`
  font-size: 16px;
  font-weight: 600;
`;

const ModalBody = styled.div`
  max-height: 60vh;
  overflow: auto;
  border: 1px solid var(--gray-5);
  border-radius: 8px;
  padding: 12px;
  background: var(--gray-1);
`;

const LoaderWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding: 24px 0;
`;

const OutputContainer = styled.div`
  margin-top: 8px;

  pre {
    margin: 0;
    white-space: pre-wrap;
    font-size: 12px;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
`;

const ErrorText = styled(Text)`
  color: var(--red-10);
`;
